import {useDispatch, useSelector} from 'react-redux';
import {RootState} from '../../../../../redux/store';
import {useMemo, useState, useRef, useCallback, useEffect} from 'react';
import {GridSortModel} from '@mui/x-data-grid';
import {Box, Typography} from '@mui/material';

import {DataGridPro, GridDataSource, useGridApiRef} from '@mui/x-data-grid-pro';
import CellWrapper from '../../../../common/arr-data-grid-pro/filter-components/cell-wrapper/CellWrapper';
import {
  analyticsDocPayload,
  DocDetailHistory,
  RequestType,
} from '../../../../../api/types/document.type';
import {getAnalyticsDocuments} from '../../../../../api/endpoints/documentApi';
import ActionCell from '../../../doc-list/action-cell-component/ActionCell';
import './DocAnalyticsDataGrid.scss';
import {IDocViewDocListRowData} from '../../../doc-list/interfaces';
import {
  setDocAnalyticsCheckedDocsConceptuallySimilar,
  setDocAnalyticsCheckedDocsFamilyMembers,
  setDocAnalyticsCheckedDocsNearDupe,
  setDocAnalyticsPreviousId,
} from '../../../../../redux/slice/docViewSlice';
import {NoRowsOverlayWithCustomLabel} from '../../doc-details/metadata/Metadata';
import {getDocAnalyticsDocumentId} from '../../../docViewerUtils';

function sortRowsClientSide(rows, sortModel) {
  if (!sortModel || sortModel.length === 0) return rows;

  const {field, sort} = sortModel[0]; // only one sort level supported

  if (!field || !sort) return rows;

  const sorted = [...rows].sort((a, b) => {
    const av = a[field];
    const bv = b[field];

    if (av == null) return 1;
    if (bv == null) return -1;

    if (av < bv) return sort === 'asc' ? -1 : 1;
    if (av > bv) return sort === 'asc' ? 1 : -1;
    return 0;
  });

  return sorted;
}

export type MiniDocRowSelection = {
  documentId?: string;
  rowNumber?: number;
  isCompleted?: boolean;
  docId?: string;
  fileName?: string;
  fileTypeCode?: string;
  miniMediaLength?: string;
  miniAudioTrimmedLength?: string;
  miniAudioDocDateTimeOnly?: string;
  docExt?: string | null;
};
export type MiniDocListColumnInfo = {
  data?: string;
  title?: string;
  visible?: boolean;
};

export type LazyDataGridApi = {
  scrollToIndex: (rowIndex: number) => void;
  reloadData: () => void;
  getRow: (uniqueKey: string | number) => any;
  updateRow: (rows: any) => void;
};

const DEFAULT_DATA_CHUNK_SIZE = 20;

const DocAnalyticsDataGrid = ({
  width,
  requestType,
  noDataLabel,
}: {
  width: number;
  requestType: RequestType;
  noDataLabel?: string;
}) => {
  const activeDocumentObj = useSelector(
    (state: RootState) => state.docView.activeDoc
  );
  const sourceId = useSelector((state: RootState) => state.docView.sourceId);
  const checkedDocsList = useSelector((state: RootState) => {
    if (requestType == 'FAMILYMEMBER') {
      return state.docView.docAnalytics.checkedDocsFamilymembers;
    } else if (requestType == 'NEARDUPLICATE') {
      return state.docView.docAnalytics.checkedDocsNearDupe;
    } else if (requestType == 'CONCEPTUALLYSIMILAR') {
      return state.docView.docAnalytics.checkedDocsConceptuallySimilar;
    }
  });
  const previouslySelectedDocId = useSelector(
    (state: RootState) => state.docView.docAnalytics.previouslySelectedDocId
  );
  const codeSameAsDocs = useSelector(
    (state: RootState) => state.docView.codeSameAsDocs
  );
  const completedDocs =
    useSelector((state: RootState) => state.docView.completedDocs) ?? [];

  const docAnalyticsRefreshKey = useSelector(
    (state: RootState) => state.docView.docAnalyticsRefreshKey
  );

  const codeSameAsDocuments = codeSameAsDocs?.map(
    (data: MiniDocRowSelection) => data?.documentId?.toString() ?? ''
  );
  const codeSameAsKey = JSON.stringify(codeSameAsDocs);
  const documentId = getDocAnalyticsDocumentId(
    activeDocumentObj?.documentId,
    previouslySelectedDocId
  );
  const dispatch = useDispatch();
  const apiRef = useGridApiRef();
  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const sortModelRef = useRef<GridSortModel>([]);
  const cacheRef = useRef<Map<number, DocDetailHistory[]>>(new Map());
  const pagesRequestedRef = useRef<Set<number>>(new Set());
  const totalRowsRef = useRef<number>(0);
  const nearDupeColumns = [
    {
      data: 'documentId',
      title: 'DocId',
      minWidth: 120,
      maxWidth: 180,
      sortable: true,
      type: 'string',
      flex: 0.4,
    },
    {
      data: 'percent',
      title: 'Percentage',
      minWidth: 100,
      maxWidth: 420,
      sortable: false,
      flex: 1,
    },
    {
      data: 'fileName',
      title: 'Filename',
      minWidth: 200,
      maxWidth: 420,
      sortable: true,
      type: 'string',
      flex: 1,
    },
  ];
  const familyMemberColumns = [
    {
      data: 'documentId',
      title: 'Doc ID',
      minWidth: 120,
      maxWidth: 180,
      sortable: true,
      type: 'string',
      flex: 0.4,
    },
    {
      data: 'fileName',
      title: 'Filename',
      minWidth: 200,
      maxWidth: 420,
      sortable: true,
      type: 'string',
      flex: 1,
    },
  ];
  const conceptuallySimilarColumns = [
    {
      data: 'documentId',
      title: 'DocId',
      minWidth: 120,
      maxWidth: 180,
      sortable: true,
      type: 'string',
      flex: 0.4,
    },
    {
      data: 'fileName',
      title: 'Filename',
      minWidth: 200,
      maxWidth: 420,
      sortable: true,
      type: 'string',
      flex: 1,
    },
  ];

  const checkedDocuments = useRef<any[]>(checkedDocsList || []);
  // Keep latest sortModel in a ref to avoid recreating fetchPage
  useEffect(() => {
    sortModelRef.current = sortModel;
  }, [sortModel]);

  useEffect(() => {
    checkedDocuments.current = checkedDocsList || [];
    forceUpdate?.();
  }, [checkedDocsList]);

  // Use custom hook:
  function useForceUpdate() {
    const [, setState] = useState({});
    return () => setState({});
  }

  const forceUpdate = useForceUpdate();

  const setCheckedDocsIdHandler = (checkedDoc: MiniDocRowSelection[]) => {
    checkedDocuments.current = checkedDoc;

    if (requestType == 'FAMILYMEMBER') {
      dispatch(
        setDocAnalyticsCheckedDocsFamilyMembers(checkedDocuments.current)
      );
    } else if (requestType == 'NEARDUPLICATE') {
      dispatch(setDocAnalyticsCheckedDocsNearDupe(checkedDocuments.current));
    } else if (requestType == 'CONCEPTUALLYSIMILAR') {
      dispatch(
        setDocAnalyticsCheckedDocsConceptuallySimilar(checkedDocuments.current)
      );
    }
  };

  const handleCheckboxClick = useCallback((rowData: IDocViewDocListRowData) => {
    const hasChecked = checkedDocuments.current.some(
      (checkedDoc) => checkedDoc?.docId == rowData.documentId
    );
    let checkedDocs: any[] = [];
    if (hasChecked) {
      checkedDocs = checkedDocuments.current.filter(
        (checkedDoc) => checkedDoc?.docId != rowData.documentId
      );
      apiRef.current?.updateRows([
        {
          id: rowData.id,
          IsChecked: false,
        },
      ]);
    } else {
      checkedDocs = [
        ...checkedDocuments.current,
        {
          docId: rowData.documentId,
          rowNumber: rowData?.RowNumber,
          isCompleted: rowData?.isCompleted,
          documentId: rowData.hDocumentId,
          fileName: rowData.fileName,
          fileTypeCode: rowData.fileType,
        },
      ];
      apiRef.current?.updateRows([
        {
          id: rowData.id,
          IsChecked: true,
        },
      ]);
    }
    setCheckedDocsIdHandler(checkedDocs);
  }, []);

  const docViewListGridColumn = useCallback(
    (nearDupeColumns: any) => {
      const actionColumn = {
        field: '__action__',
        headerName: '',
        cellClassName: 'action-cell',
        minWidth: 60,
        maxWidth: 60,
        renderCell: ({row}) => {
          const isCompleted = completedDocs.some(
            (completedDoc) => completedDoc.docId === row.documentId
          );

          const modifiedRow = {
            ...row,
            IsCodeSame: row.isCodeSame,
            IsCompleted:
              row.IsCompleted == true || row.IsCompleted == 1 || isCompleted,
          };

          return (
            <ActionCell
              rowData={modifiedRow}
              onCheckboxClick={handleCheckboxClick}
              actionCellId={`analytics-grid-${row.id}`}
            />
          );
        },
      };

      const selectedColumns = [];
      for (const [index, col] of nearDupeColumns.entries()) {
        selectedColumns.push({
          field: col.data,
          headerName: col.title,
          headerClassName:
            index == nearDupeColumns.length - 1 ? 'last-column ' : '',
          flex: col.flex,
          minWidth: col.minWidth,
          maxWidth: col.maxWidth,
          sortable: col.sortable,
          renderCell: (param) => {
            return (
              <CellWrapper
                toolTipContent={param.value}
                dataTestId={`cell-wrapper-tooltip-content-analytics-${col.data}-${param.id}`}
                cellParams={param}
              >
                <Typography
                  className=" custom-cell-content typo-text body4_size"
                  data-testid={`doc-analytics-data-grid-cell-${col.data}-${param.id}`}
                >
                  {param.value}
                </Typography>
              </CellWrapper>
            );
          },
        });
      }
      return [actionColumn, ...selectedColumns];
    },
    [completedDocs]
  );
  // Reset cache & data source when documentId, sort, or refresh key changes
  useEffect(() => {
    cacheRef.current.clear();
    pagesRequestedRef.current.clear();
    totalRowsRef.current = 0;
  }, [documentId, sortModel, codeSameAsKey, docAnalyticsRefreshKey]); // Added docAnalyticsRefreshKey

  useEffect(() => {
    if (!documentId) return;
    const currentId = documentId.toString();
    if (previouslySelectedDocId && previouslySelectedDocId !== currentId) {
      dispatch(setDocAnalyticsCheckedDocsFamilyMembers([]));
      dispatch(setDocAnalyticsCheckedDocsNearDupe([]));
      dispatch(setDocAnalyticsCheckedDocsConceptuallySimilar([]));
    }
    dispatch(setDocAnalyticsPreviousId(currentId));
  }, [
    documentId,
    previouslySelectedDocId,
    previouslySelectedDocId,
    checkedDocsList,
    codeSameAsKey,
  ]);

  function flattenApiResult(apiResponse) {
    // apiResponse.result is expected to be an array
    const arr = Array.isArray(apiResponse?.result) ? apiResponse.result : [];
    return arr.map((item) => {
      const panel = item.analyticsPanelResult ?? {};
      // merge panel fields first, then top-level fields override if same key exists
      return {
        ...panel,
        // keep other top-level fields explicitly
        hiddenDocId: item.hiddenDocId,
        percent: item.percent,
      };
    });
  }

  // Fetch page data only once per page, stop if beyond totalRows
  const fetchPage = useCallback(
    async (pageNum: number): Promise<any[]> => {
      if (pagesRequestedRef.current.has(pageNum)) {
        return cacheRef.current.get(pageNum) || [];
      }

      pagesRequestedRef.current.add(pageNum);

      const displayLength = pageNum * DEFAULT_DATA_CHUNK_SIZE;

      const payload: analyticsDocPayload = {
        documentId: Number(documentId),
        requestType: requestType,
        maxCols: 0,
        maxRows: 0,
        displayLength,
        docViewSessionObjects: {
          documentListMasterID: 0,
          sourceTypeCode: '',
          docsForCodeSame: codeSameAsDocuments ?? [''],
          assignmentSource: '',
          sourceID: sourceId,
          userCodingFormID: 0,
          xmlCompletedDocumentEditedCodingForm: '',
        },
      };

      try {
        const response = await getAnalyticsDocuments(payload);

        const fullRows = flattenApiResult(response);

        // ⬇ TOTAL COUNT from API
        const total =
          response?.result?.[0]?.analyticsPanelResult?.totalDocuments ||
          fullRows.length;
        totalRowsRef.current = total;

        // ⬇ Extract ONLY the actual page’s part of data
        const startIndex = (pageNum - 1) * DEFAULT_DATA_CHUNK_SIZE;
        const endIndex = pageNum * DEFAULT_DATA_CHUNK_SIZE;

        let pageSlice = fullRows.slice(startIndex, endIndex);

        // 👇 Apply client-side sorting here
        pageSlice = sortRowsClientSide(pageSlice, sortModelRef.current);

        // Save to cache
        cacheRef.current.set(pageNum, pageSlice);

        return pageSlice;
      } catch (err) {
        console.warn('error', err);
        cacheRef.current.set(pageNum, []);
        return [];
      }
    },
    [documentId, codeSameAsKey]
  );

  // Stable dataSource for DataGridPro
  const dataSource: GridDataSource = useMemo(
    () => ({
      getRows: async ({start, end}) => {
        const startPage =
          Math.floor(Number(start) / DEFAULT_DATA_CHUNK_SIZE) + 1;
        const endPage = Math.floor(end / DEFAULT_DATA_CHUNK_SIZE) + 1;

        const allRows: DocDetailHistory[] = [];

        for (let page = startPage; page <= endPage; page++) {
          const pageRows = await fetchPage(page);
          allRows.push(...pageRows);
        }

        const sortedAllRows = sortRowsClientSide(allRows, sortModelRef.current);

        // Now slice for visible window
        const visibleRows = sortedAllRows.slice(
          Number(start) % DEFAULT_DATA_CHUNK_SIZE,
          end - Number(start) + 1
        );

        return {
          rows: visibleRows.map((row, i) => {
            const isChecked = checkedDocuments.current.some(
              (item) => item.docId === row.documentId
            );
            const isCompleted = completedDocs.some(
              (completedDoc) => completedDoc.docId === row.documentId
            );

            return {
              ...row,
              IsChecked: isChecked,
              IsCompleted:
                row.IsCompleted == true || row.isCompleted == 1 || isCompleted,
              id: `${row.hiddenDocId}-${Number(start) + i}`,
            };
          }),
          rowCount: totalRowsRef.current,
        };
      },
    }),
    [fetchPage, completedDocs, checkedDocsList]
  );

  const updatedColumns =
    requestType === 'FAMILYMEMBER'
      ? familyMemberColumns
      : requestType === 'NEARDUPLICATE'
        ? nearDupeColumns
        : conceptuallySimilarColumns;

  const columns = docViewListGridColumn(updatedColumns);

  const noRowsOverlay = () => (
    <NoRowsOverlayWithCustomLabel noDataLabel={noDataLabel} />
  );

  return (
    <Box
      width={width}
      className="meta-data-container"
      data-testid="history-grid-wrapper"
      tabIndex={0}
    >
      <DataGridPro
        key={`${documentId ?? 'no-doc'}-${docAnalyticsRefreshKey}`} // Added refresh key
        apiRef={apiRef}
        getRowId={(row) => row.id}
        data-testid="history-tab-grid"
        columns={columns}
        dataSource={dataSource}
        lazyLoading
        slots={{
          headerFilterMenu: null,
          noRowsOverlay,
        }}
        slotProps={{
          loadingOverlay: {variant: 'skeleton', noRowsVariant: 'skeleton'},
        }}
        paginationModel={{page: 0, pageSize: 20}}
        sortingOrder={['asc', 'desc']}
        sortModel={sortModel}
        onSortModelChange={setSortModel}
        disableColumnFilter
        disableColumnMenu
        disableColumnResize
        disableColumnReorder
        disableRowSelectionOnClick
        rowHeight={36}
        hideFooter
        hideFooterPagination
        hideFooterRowCount
        getRowClassName={(params) => {
          const isActiveRow =
            activeDocumentObj?.documentId != null &&
            (String(params.row.hiddenDocId) ===
              String(activeDocumentObj.documentId) ||
              String(params.row.documentId) === String(activeDocumentObj.docId));

          if (isActiveRow) {
            return 'row-selected';
          }
          if (params.row.isCodeSame) {
            return 'row-code-same-as';
          }
          return '';
        }}
      />
    </Box>
  );
};

export default DocAnalyticsDataGrid;
